import { ApiError } from "@/api/client";

// Only override where camelCase → Title Case produces a confusing label.
// Auto-conversion handles the rest (e.g., "shortDescription" → "Short Description").
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  destinationId: "Destination",
  guideId: "Guide",
  parentId: "Parent destination",
  tripId: "Trip",
  priceFrom: "Price",
  priceOriginal: "Original price",
  priceEur: "Price (EUR)",
  durationDays: "Duration (days)",
  groupSizeMin: "Min. group size",
  groupSizeMax: "Max. group size",
  heroVideoUrl: "Hero video URL",
  guideVideoUrl: "Guide video URL",
  metaTitle: "SEO title",
  metaDescription: "SEO description",
  yearsExperience: "Experience (years)",
  readTime: "Read time (min)",
  isFeatured: "Featured",
  isLastMinute: "Last minute",
  isActive: "Active",
  hasCzechGuide: "Czech guide",
  allowsOnlineBooking: "Online booking",
};

/** Convert camelCase to Title Case: "priceFrom" → "Price from" */
function camelToTitle(str: string): string {
  return str
    .replace(/Id$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Get a human-readable field label from a camelCase field name. */
export function getFieldLabel(fieldName: string): string {
  return FIELD_LABEL_OVERRIDES[fieldName] || camelToTitle(fieldName);
}

// Common Zod error patterns → user-friendly messages
const ZOD_MESSAGE_MAP: [RegExp, string][] = [
  [/^Required$/, "This field is required"],
  [/^String must contain at least 1 character/, "This field is required"],
  [/^Expected number, received (null|string)/, "Please enter a valid number"],
  [/^Number must be greater than or equal to (\d+)/, "Must be $1 or more"],
  [/^Number must be less than or equal to (\d+)/, "Must be $1 or less"],
  [/^String must contain at most (\d+) character/, "Maximum $1 characters"],
  [/^String must contain at least (\d+) character/, "Minimum $1 characters"],
  [/^Invalid enum value/, "Please select a valid option"],
  [/^Invalid url/, "Please enter a valid URL"],
  [/^Invalid email/, "Please enter a valid email"],
  [/^Expected .+, received .+/, "Invalid value"],
];

/** Simplify a Zod validation message to plain English. */
export function humanizeFieldMessage(zodMessage: string): string {
  for (const [pattern, replacement] of ZOD_MESSAGE_MAP) {
    const match = zodMessage.match(pattern);
    if (match) {
      // Replace $1 with first capture group if present
      return replacement.replace("$1", match[1] || "");
    }
  }
  return zodMessage;
}

/** Get a user-friendly summary message based on error type. */
export function getUserMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNetwork) return "Cannot connect to server. Check your internet connection.";
    if (error.isValidation) {
      return error.fieldErrors
        ? "Please fix the errors highlighted below."
        : "Please check the form — some data is not valid.";
    }
    if (error.isAuth) return "Your session has expired. Please log in again.";
    if (error.isForbidden) return "You don't have permission for this action.";
    if (error.isNotFound) return "This item was not found. It may have been deleted.";
    if (error.isServer) return "Something went wrong on our end. Please try again later.";
  }
  if (error instanceof Error) {
    return error.message || "An unexpected error occurred.";
  }
  return "An unexpected error occurred.";
}
